import { useState } from "react";
import { 
  Building2, 
  Phone, 
  Headphones, 
  Moon, 
  Calendar,
  Sparkles,
  ChevronRight,
  Check,
  Mic,
  Bot,
  TrendingUp,
  Users,
  Clock,
  MessageSquare,
  Filter,
  RotateCcw
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Functional Categories for Agent Templates
export const TEMPLATE_CATEGORIES = [
  { id: "all", name: "All Templates", icon: Sparkles },
  { id: "sales", name: "Sales & Lead Gen", icon: TrendingUp },
  { id: "support", name: "Customer Support", icon: Headphones },
  { id: "operations", name: "Operations", icon: Calendar },
  { id: "afterhours", name: "After-Hours", icon: Moon }
];

// Agent Preset Catalog - Organized by FUNCTION, not industry
export const AGENT_PRESETS = [
  // === SALES & LEAD GEN ===
  {
    id: "sales_outbound",
    name: "Outbound Sales",
    category: "sales",
    subtitle: "Lead Generation",
    description: "Proactively reaches out to prospects, qualifies leads, and books sales meetings.",
    icon: TrendingUp,
    color: "emerald",
    personality: "Enthusiastic, consultative, and goal-oriented sales professional",
    system_prompt: `You are a skilled outbound sales representative. Your objectives:
- Engage prospects with genuine interest and a friendly opening
- Ask qualifying questions to understand their needs and pain points
- Present value propositions clearly and concisely
- Handle objections professionally without being pushy
- Book meetings or demos with qualified leads
- Capture lead information accurately (name, company, email, phone)
Be consultative, not aggressive. Focus on understanding customer needs first.`,
    voice: "male_warm",
    use_cases: ["Cold calling", "Lead qualification", "Demo booking"],
    recommended_for: ["Any industry with outbound sales"]
  },
  {
    id: "sales_inbound",
    name: "Inbound Sales",
    category: "sales",
    subtitle: "Inquiry Handler",
    description: "Handles incoming sales inquiries, answers questions, and converts interest to meetings.",
    icon: Phone,
    color: "emerald",
    personality: "Helpful, knowledgeable, and conversion-focused sales assistant",
    system_prompt: `You are an inbound sales specialist. Your responsibilities:
- Welcome callers warmly and professionally
- Understand their interest and what brought them to call
- Answer questions about products/services accurately
- Identify their timeline and budget when appropriate
- Guide qualified prospects to next steps (demo, consultation, quote)
- Capture contact information for follow-up
Be helpful and informative. Your goal is to convert interest into action.`,
    voice: "female_professional",
    use_cases: ["Sales inquiries", "Product questions", "Quote requests"],
    recommended_for: ["Any business receiving sales calls"]
  },
  {
    id: "lead_qualifier",
    name: "Lead Qualifier",
    category: "sales",
    subtitle: "BANT Qualification",
    description: "Qualifies leads using structured criteria (Budget, Authority, Need, Timeline).",
    icon: Users,
    color: "emerald",
    personality: "Methodical, friendly, and efficient lead qualification specialist",
    system_prompt: `You are a lead qualification specialist using the BANT framework. Your process:
- Greet the prospect warmly
- Ask about their NEED: What problem are they trying to solve?
- Explore AUTHORITY: Are they the decision maker?
- Understand BUDGET: Do they have budget allocated?
- Determine TIMELINE: When do they need a solution?
- Score leads as Hot (ready now), Warm (3-6 months), or Cold (just exploring)
- Capture all relevant details for the sales team
Be conversational, not interrogative. Make it feel like a helpful discovery call.`,
    voice: "female_warm",
    use_cases: ["Lead scoring", "Discovery calls", "Sales qualification"],
    recommended_for: ["B2B companies", "High-value sales"]
  },

  // === CUSTOMER SUPPORT ===
  {
    id: "support_general",
    name: "General Support",
    category: "support",
    subtitle: "Help Desk",
    description: "Handles general customer inquiries, FAQs, and basic troubleshooting.",
    icon: Headphones,
    color: "purple",
    personality: "Patient, empathetic, and helpful customer support specialist",
    system_prompt: `You are a customer support specialist. Your responsibilities:
- Greet customers warmly and ask how you can help
- Listen carefully to their issue or question
- Provide clear, accurate answers to common questions
- Walk through basic troubleshooting steps when needed
- Know when to escalate to a human specialist
- Always confirm the customer's issue is resolved before ending
Stay calm, be patient, and focus on solving their problem.`,
    voice: "female_warm",
    use_cases: ["General inquiries", "FAQ handling", "Basic troubleshooting"],
    recommended_for: ["Any business with customer support"]
  },
  {
    id: "support_technical",
    name: "Technical Support",
    category: "support",
    subtitle: "Tier 1 Tech",
    description: "Provides first-level technical support with structured troubleshooting.",
    icon: MessageSquare,
    color: "purple",
    personality: "Technical, patient, and systematic troubleshooter",
    system_prompt: `You are a Tier 1 technical support agent. Your approach:
- Greet the customer and get their account/case information
- Ask them to describe the issue in detail
- Follow structured troubleshooting: restart, update, reinstall
- Guide them through steps clearly, one at a time
- Document the issue and steps tried
- Escalate to Tier 2 if the issue persists after basic troubleshooting
Be patient with non-technical users. Explain steps in simple terms.`,
    voice: "male_warm",
    use_cases: ["Technical issues", "Product troubleshooting", "Setup assistance"],
    recommended_for: ["Tech companies", "SaaS", "Software providers"]
  },
  {
    id: "support_billing",
    name: "Billing Support",
    category: "support",
    subtitle: "Account & Payments",
    description: "Handles billing inquiries, payment issues, and account questions.",
    icon: Building2,
    color: "purple",
    personality: "Professional, detail-oriented, and helpful billing specialist",
    system_prompt: `You are a billing support specialist. Your responsibilities:
- Verify the customer's identity securely
- Answer questions about invoices and charges
- Explain billing cycles and payment methods
- Help with payment failures or declined cards
- Process refund requests within policy guidelines
- Update payment methods when authorized
Be professional and accurate. Never share sensitive payment details verbally.`,
    voice: "female_professional",
    use_cases: ["Billing questions", "Payment issues", "Account changes"],
    recommended_for: ["Subscription businesses", "Service providers"]
  },

  // === OPERATIONS & SCHEDULING ===
  {
    id: "receptionist",
    name: "Receptionist",
    category: "operations",
    subtitle: "Front Desk",
    description: "Professional front-desk assistant that handles calls, routes inquiries, and takes messages.",
    icon: Building2,
    color: "blue",
    personality: "Professional, friendly, and efficient receptionist",
    system_prompt: `You are a professional receptionist. Your responsibilities:
- Answer calls warmly: "Thank you for calling [Company], how may I help you?"
- Gather caller information (name, reason for calling)
- Route calls to the appropriate person or department
- Take detailed messages when staff are unavailable
- Provide basic business information (hours, location, services)
- Handle multiple priorities professionally
Keep responses concise and helpful. Always confirm understanding before ending calls.`,
    voice: "female_professional",
    use_cases: ["Call answering", "Call routing", "Message taking"],
    recommended_for: ["Any business needing reception services"]
  },
  {
    id: "scheduler",
    name: "Appointment Scheduler",
    category: "operations",
    subtitle: "Booking Specialist",
    description: "Specializes in scheduling, rescheduling, and managing appointments efficiently.",
    icon: Calendar,
    color: "amber",
    personality: "Organized, accommodating, and detail-oriented scheduling specialist",
    system_prompt: `You are an appointment scheduling specialist. Your responsibilities:
- Help callers book new appointments efficiently
- Check availability and offer suitable time slots
- Handle rescheduling requests gracefully
- Confirm appointment details: date, time, location, purpose
- Send appointment confirmations and reminders
- Manage cancellations and waitlist additions
Be accommodating while respecting scheduling constraints.`,
    voice: "female_warm",
    use_cases: ["Appointment booking", "Rescheduling", "Calendar management"],
    recommended_for: ["Healthcare", "Salons", "Professional services"]
  },
  {
    id: "reminder_agent",
    name: "Reminder Agent",
    category: "operations",
    subtitle: "Follow-up Calls",
    description: "Makes outbound reminder calls for appointments, payments, or renewals.",
    icon: Clock,
    color: "amber",
    personality: "Friendly, clear, and efficient reminder specialist",
    system_prompt: `You are a reminder call specialist. Your approach:
- Introduce yourself and the business clearly
- State the purpose of the call (appointment reminder, payment due, etc.)
- Confirm the details with the customer
- Ask if they need to reschedule or have questions
- Capture any updates or changes they request
- Thank them for their time
Be friendly and efficient. Keep calls brief but complete.`,
    voice: "female_warm",
    use_cases: ["Appointment reminders", "Payment reminders", "Renewal calls"],
    recommended_for: ["Healthcare", "Service businesses", "Subscription companies"]
  },

  // === AFTER-HOURS & EMERGENCY ===
  {
    id: "afterhours_general",
    name: "After-Hours",
    category: "afterhours",
    subtitle: "Night & Weekend",
    description: "Handles calls outside business hours, takes messages, and triages urgent matters.",
    icon: Moon,
    color: "indigo",
    personality: "Calm, reassuring, and efficient after-hours assistant",
    system_prompt: `You are an after-hours assistant. Your responsibilities:
- Inform callers of current business hours
- Assess the urgency of their call
- Take detailed messages for next business day follow-up
- Handle true emergencies according to protocol
- Provide estimated callback times
- Capture callback preferences and best times to reach them
Be reassuring and set clear expectations. Distinguish between urgent and routine matters.`,
    voice: "female_professional",
    use_cases: ["Night coverage", "Weekend calls", "Message taking"],
    recommended_for: ["Any business needing after-hours coverage"]
  },
  {
    id: "emergency_triage",
    name: "Emergency Triage",
    category: "afterhours",
    subtitle: "Urgent Response",
    description: "Handles emergency calls, assesses severity, and routes to appropriate response.",
    icon: Phone,
    color: "indigo",
    personality: "Calm, focused, and decisive emergency response specialist",
    system_prompt: `You are an emergency triage specialist. Your protocol:
- Answer with urgency awareness: "Emergency line, how can I help?"
- Quickly assess the nature and severity of the emergency
- For life-threatening emergencies: Advise calling 911 immediately
- For urgent but non-life-threatening: Capture details and escalate to on-call staff
- For non-urgent matters: Take a message and set expectations
- Always get callback number and location
Stay calm. Your composure helps callers stay calm too.`,
    voice: "male_professional",
    use_cases: ["Emergency calls", "Urgent triage", "On-call escalation"],
    recommended_for: ["Healthcare", "Property management", "Service businesses"]
  }
];

// Color mappings
const COLOR_CLASSES = {
  blue: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    text: "text-blue-400",
    icon: "text-blue-400"
  },
  emerald: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    text: "text-emerald-400",
    icon: "text-emerald-400"
  },
  purple: {
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    text: "text-purple-400",
    icon: "text-purple-400"
  },
  indigo: {
    bg: "bg-indigo-500/10",
    border: "border-indigo-500/30",
    text: "text-indigo-400",
    icon: "text-indigo-400"
  },
  amber: {
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    text: "text-amber-400",
    icon: "text-amber-400"
  }
};

/**
 * Preset selection card for agent creation
 */
export function PresetCard({ preset, selected, onSelect, disabled, compact = false }) {
  const colors = COLOR_CLASSES[preset.color] || COLOR_CLASSES.blue;
  const Icon = preset.icon;

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200",
        "hover:border-primary/50",
        selected ? "ring-2 ring-primary border-primary/50" : "border-border",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      onClick={() => !disabled && onSelect(preset)}
      data-testid={`preset-card-${preset.id}`}
    >
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex items-start gap-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", colors.bg)}>
            <Icon className={cn("w-5 h-5", colors.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">{preset.name}</h3>
              {selected && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-primary-foreground" />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{preset.subtitle}</p>
            {!compact && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{preset.description}</p>
            )}
          </div>
        </div>
        
        {/* Use cases */}
        {!compact && (
          <div className="flex flex-wrap gap-1 mt-3">
            {preset.use_cases.slice(0, 3).map((useCase) => (
              <Badge key={useCase} variant="outline" className="text-xs">
                {useCase}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Category filter tabs
 */
export function CategoryFilter({ selectedCategory, onSelectCategory }) {
  return (
    <div className="flex flex-wrap gap-2 justify-center" data-testid="category-filter">
      {TEMPLATE_CATEGORIES.map((category) => {
        const Icon = category.icon;
        const isSelected = selectedCategory === category.id;
        return (
          <Button
            key={category.id}
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={() => onSelectCategory(category.id)}
            className={cn(
              "transition-all",
              isSelected && "shadow-md"
            )}
            data-testid={`category-${category.id}`}
          >
            <Icon className="w-4 h-4 mr-1.5" />
            {category.name}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * Preset selection step component with category filtering
 */
export function PresetSelector({ selectedPreset, onSelectPreset, onSkip }) {
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  const filteredPresets = selectedCategory === "all" 
    ? AGENT_PRESETS 
    : AGENT_PRESETS.filter(p => p.category === selectedCategory);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Choose a Template</h2>
        </div>
        <p className="text-muted-foreground">
          Select a template organized by function, or start from scratch.
        </p>
      </div>

      {/* Category Filter */}
      <CategoryFilter 
        selectedCategory={selectedCategory} 
        onSelectCategory={setSelectedCategory} 
      />

      {/* Preset Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto pr-2">
        {filteredPresets.map((preset) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            selected={selectedPreset?.id === preset.id}
            onSelect={onSelectPreset}
          />
        ))}
      </div>

      {/* Results count */}
      <p className="text-center text-sm text-muted-foreground">
        Showing {filteredPresets.length} template{filteredPresets.length !== 1 ? 's' : ''}
        {selectedCategory !== "all" && (
          <Button 
            variant="link" 
            size="sm" 
            className="p-0 h-auto ml-2"
            onClick={() => setSelectedCategory("all")}
          >
            Show all
          </Button>
        )}
      </p>

      {/* Custom Option */}
      <div className="flex items-center justify-center">
        <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
          <Bot className="w-4 h-4 mr-2" />
          Start from scratch (advanced)
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Template switcher for existing agents - allows changing template
 */
export function TemplateSwitcher({ currentTemplate, onSelectTemplate, onCancel }) {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  const filteredPresets = selectedCategory === "all" 
    ? AGENT_PRESETS 
    : AGENT_PRESETS.filter(p => p.category === selectedCategory);

  const handleApply = () => {
    if (selectedPreset) {
      onSelectTemplate(selectedPreset);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <RotateCcw className="w-4 h-4" />
        <span>Switch to a different template. Your custom changes will be replaced.</span>
      </div>

      {/* Category Filter - Compact */}
      <div className="flex flex-wrap gap-1">
        {TEMPLATE_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategory === category.id;
          return (
            <Button
              key={category.id}
              variant={isSelected ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className="h-7 text-xs"
            >
              <Icon className="w-3 h-3 mr-1" />
              {category.name}
            </Button>
          );
        })}
      </div>

      {/* Preset Grid - Compact */}
      <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto">
        {filteredPresets.map((preset) => (
          <PresetCard
            key={preset.id}
            preset={preset}
            selected={selectedPreset?.id === preset.id}
            onSelect={setSelectedPreset}
            compact
          />
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleApply} disabled={!selectedPreset}>
          Apply Template
        </Button>
      </div>
    </div>
  );
}

/**
 * Preset preview component
 */
export function PresetPreview({ preset }) {
  if (!preset) return null;
  
  const colors = COLOR_CLASSES[preset.color] || COLOR_CLASSES.blue;
  const Icon = preset.icon;
  const category = TEMPLATE_CATEGORIES.find(c => c.id === preset.category);

  return (
    <Card className={cn("border", colors.border)}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", colors.bg)}>
            <Icon className={cn("w-6 h-6", colors.icon)} />
          </div>
          <div>
            <CardTitle className="text-lg">{preset.name}</CardTitle>
            <CardDescription className="flex items-center gap-2">
              {preset.subtitle}
              {category && (
                <Badge variant="outline" className="text-xs">
                  {category.name}
                </Badge>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-1">Personality</p>
          <p className="text-sm text-muted-foreground">{preset.personality}</p>
        </div>
        
        <div>
          <p className="text-sm font-medium mb-1">Voice</p>
          <Badge variant="outline" className="text-xs">
            <Mic className="w-3 h-3 mr-1" />
            {preset.voice.replace("_", " ")}
          </Badge>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Best For</p>
          <div className="flex flex-wrap gap-1">
            {preset.recommended_for.map((use) => (
              <Badge key={use} className={cn("text-xs", colors.bg, colors.text)}>
                {use}
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default AGENT_PRESETS;
